import { z } from 'zod';

/** IBAN: 2 letters (country) + 2 digits (check) + 11–30 alphanumeric BBAN (15–34 chars total). */
export const IBAN_FORMAT_REGEX = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/;

/** Shortest registered national IBAN (Norway); ISO 13616 max is 34. */
export const IBAN_MIN_LENGTH = 15;
export const IBAN_MAX_LENGTH = 34;

export const BANK_ACCOUNT_NUMBER_MIN_DIGITS = 3;
export const BANK_ACCOUNT_NUMBER_MAX_DIGITS = 33;

export type BankDetailsTranslateFn = (key: string) => string;

/** Trim, strip internal whitespace, and uppercase bank identifiers (IBAN, SWIFT, account input). */
export function trimWhitespace(raw: string | undefined | null): string {
  if (!raw) return '';
  return raw.trim().replace(/\s+/g, '').toUpperCase();
}

/** Strip non-digits for display/API alignment with numeric-only account field. */
export function normalizeBankAccountNumberInput(raw: string | undefined | null): string {
  return String(raw ?? '').replace(/\D/g, '');
}

export function hasInvalidAccountSeparators(value: string): boolean {
  return /[\s-]/.test(value);
}

/** IBAN structure check (regex only). */
export function isValidIbanFormat(normalizedIban: string): boolean {
  return IBAN_FORMAT_REGEX.test(normalizedIban);
}

/**
 * ISO 13616 MOD 97-10 checksum. `normalizedIban` must be A–Z and 0–9 only.
 */
export function validateIbanChecksum(normalizedIban: string): boolean {
  const rearranged = normalizedIban.slice(4) + normalizedIban.slice(0, 4);
  let expanded = '';
  for (const ch of rearranged) {
    if (ch >= 'A' && ch <= 'Z') {
      expanded += (ch.charCodeAt(0) - 55).toString();
    } else if (ch >= '0' && ch <= '9') {
      expanded += ch;
    } else {
      return false;
    }
  }
  let remainder = 0;
  for (let i = 0; i < expanded.length; i++) {
    const digit = expanded.charAt(i);
    remainder = (remainder * 10 + parseInt(digit, 10)) % 97;
  }
  return remainder === 1;
}

/** Full IBAN validation: structure + checksum. */
export function isIbanFullyValid(normalizedIban: string): boolean {
  return isValidIbanFormat(normalizedIban) && validateIbanChecksum(normalizedIban);
}

/** Detects IBAN intent for routing validation errors (prefix only). */
export function looksLikeIbanAttempt(normalizedAccount: string): boolean {
  return /^[A-Z]{2}[0-9]{2}/.test(normalizedAccount);
}

/** Local bank account: 3–33 digits (numeric only). */
export function isValidBankAccountFormat(accountTrim: string): boolean {
  return (
    accountTrim.length >= BANK_ACCOUNT_NUMBER_MIN_DIGITS &&
    accountTrim.length <= BANK_ACCOUNT_NUMBER_MAX_DIGITS &&
    /^[0-9]+$/.test(accountTrim)
  );
}

export function isValidSwiftFormat(normalizedSwift: string): boolean {
  if (normalizedSwift.length !== 8 && normalizedSwift.length !== 11) return false;
  return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(normalizedSwift);
}

export function createBankNameSchema(t: BankDetailsTranslateFn) {
  return z
    .string()
    .trim()
    .min(1, { message: t('bankNameRequired') })
    .max(255, { message: t('bankNameMaxLength') });
}

export function createSwiftBicSchema(t: BankDetailsTranslateFn) {
  return z
    .string()
    .trim()
    .superRefine((value, ctx) => {
      const normalizedSwift = trimWhitespace(value);
      if (!normalizedSwift || isValidSwiftFormat(normalizedSwift)) return;

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('swiftBicInvalidFormat'),
      });
    })
    .optional();
}

/** Unified field schema: a valid IBAN or a 3–33 digit local bank account number. */
export function createAccountNumberOrIbanSchema(t: BankDetailsTranslateFn) {
  return z
    .string()
    .trim()
    .superRefine((rawValue, ctx) => {
      if (hasInvalidAccountSeparators(rawValue)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('bankAccountNoSpacesOrDashes'),
        });
        return;
      }

      const normalizedAccount = trimWhitespace(rawValue);

      if (looksLikeIbanAttempt(normalizedAccount)) {
        if (
          normalizedAccount.length < IBAN_MIN_LENGTH ||
          normalizedAccount.length > IBAN_MAX_LENGTH
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('ibanInvalidLength'),
          });
          return;
        }

        if (!isValidIbanFormat(normalizedAccount)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('ibanInvalidFormat'),
          });
          return;
        }

        if (!validateIbanChecksum(normalizedAccount)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('ibanInvalidChecksum'),
          });
        }
        return;
      }

      if (normalizedAccount.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('bankAccountRequired'),
        });
        return;
      }

      if (isValidBankAccountFormat(normalizedAccount)) return;

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t(
          /^[0-9]+$/.test(normalizedAccount)
            ? 'bankAccountNumberLengthRange'
            : 'bankAccountNumberDigitsOnly'
        ),
      });
    });
}
